import React, { useCallback, useEffect, useRef, useState } from 'react';
import CountryFlag, { CountryLabel } from '../../../shared/components/CountryFlag';
import { Card, Row, Col, Statistic, Table, Tag, Space, Select, Input, Button, Alert, Spin, Empty, Tooltip, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import cdnToolsService from '../../services/cdnToolsService';

const ACTIONS = {
  block: { label: '拦截', color: 'red', chart: '#cf1322' },
  js_challenge: { label: 'JS 质询', color: 'orange', chart: '#fa8c16' },
  managed_challenge: { label: '托管质询', color: 'gold', chart: '#d4b106' },
  ratelimit: { label: '限速', color: 'purple', chart: '#722ed1' },
  log: { label: '仅记录', color: 'blue', chart: '#1677ff' },
};
const actionTag = (a) => {
  const m = ACTIONS[a];
  return <Tag color={m?.color}>{m?.label || a || '-'}</Tag>;
};
const num = (v) => new Intl.NumberFormat('zh-CN').format(Number(v) || 0);
// OpenObserve histogram 输出 UTC 无时区后缀
const localTime = (t, f = 'MM-DD HH:mm') => {
  if (!t) return '';
  const s = typeof t === 'string' && !t.endsWith('Z') && !t.includes('+') ? `${t}Z` : t;
  return dayjs(new Date(s)).format(f);
};

/**
 * 管理端 WAF 分析。由 Analytics 页作为 Tab 嵌入，复用其时间范围与域名筛选。
 */
export default function WafAnalytics({ dateRange, domainId }) {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [topRules, setTopRules] = useState([]);
  const [topIps, setTopIps] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ action: undefined, client_ip: '', uri: '' });
  const [applied, setApplied] = useState({});
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(20);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  const chartRef = useRef(null);
  const chart = useRef(null);

  const baseParams = useCallback(() => {
    const p = {
      startTime: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
      endTime: dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
    };
    if (domainId) p.domain_id = domainId;
    return p;
  }, [dateRange, domainId]);

  const loadSummary = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    setLoading(true);
    const params = baseParams();
    const calls = [
      ['overview', cdnToolsService.getWafOverview(params)],
      ['timeline', cdnToolsService.getWafTimeline(params)],
      ['rules', cdnToolsService.getWafTopRules({ ...params, limit: 10 })],
      ['ips', cdnToolsService.getWafTopIPs({ ...params, limit: 10 })],
    ];
    const settled = await Promise.allSettled(calls.map(([, p]) => p));
    const errs = {};
    settled.forEach((r, i) => {
      const key = calls[i][0];
      const res = r.status === 'fulfilled' ? r.value : { success: false, error: r.reason?.message };
      if (!res?.success) {
        errs[key] = res?.error || '加载失败';
        return;
      }
      if (key === 'overview') setOverview(res.data || {});
      if (key === 'timeline') setTimeline(Array.isArray(res.data) ? res.data : []);
      if (key === 'rules') setTopRules(Array.isArray(res.data) ? res.data : []);
      if (key === 'ips') setTopIps(Array.isArray(res.data) ? res.data : []);
    });
    setErrors(errs);
    if (Object.keys(errs).length) message.error(`WAF 数据部分加载失败：${Object.values(errs)[0]}`);
    setLoading(false);
  }, [baseParams, dateRange]);

  const loadLogs = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    setLogsLoading(true);
    const params = { ...baseParams(), page: logPage, pageSize: logPageSize };
    if (applied.action) params.action = applied.action;
    if (applied.client_ip) params.client_ip = applied.client_ip;
    if (applied.uri) params.uri = applied.uri;
    const res = await cdnToolsService.getWafLogs(params);
    if (res.success) {
      setLogs(Array.isArray(res.data) ? res.data : []);
      setLogTotal(Number(res.total) || 0);
      setLogsError(null);
    } else {
      setLogsError(res.error || '加载失败');
      message.error(`WAF 事件加载失败：${res.error || ''}`);
    }
    setLogsLoading(false);
  }, [baseParams, dateRange, applied, logPage, logPageSize]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { setLogPage(1); }, [dateRange, domainId]);

  // 时间线：按动作堆叠
  useEffect(() => {
    if (!chartRef.current) return undefined;
    if (!timeline.length) {
      chart.current?.dispose();
      chart.current = null;
      return undefined;
    }
    if (!chart.current) chart.current = echarts.init(chartRef.current);
    const buckets = [...new Set(timeline.map((r) => r.time_bucket))].sort();
    const actions = [...new Set(timeline.map((r) => r.fw_action || 'unknown'))];
    const idx = Object.fromEntries(buckets.map((b, i) => [b, i]));
    const series = actions.map((a) => {
      const data = new Array(buckets.length).fill(0);
      timeline.filter((r) => (r.fw_action || 'unknown') === a).forEach((r) => { data[idx[r.time_bucket]] += Number(r.count) || 0; });
      return {
        name: ACTIONS[a]?.label || a,
        type: 'bar',
        stack: 'waf',
        data,
        itemStyle: ACTIONS[a] ? { color: ACTIONS[a].chart } : undefined,
      };
    });
    chart.current.setOption({
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { left: 40, right: 16, top: 32, bottom: 28 },
      xAxis: { type: 'category', data: buckets.map((b) => localTime(b)) },
      yAxis: { type: 'value', minInterval: 1 },
      series,
    }, true);
    return undefined;
  }, [timeline]);

  useEffect(() => {
    const onResize = () => chart.current?.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.current?.dispose();
      chart.current = null;
    };
  }, []);

  const search = () => {
    setLogPage(1);
    setApplied({
      action: filters.action,
      client_ip: filters.client_ip.trim(),
      uri: filters.uri.trim(),
    });
  };

  const stats = [
    ['总事件', overview?.total_events],
    ['拦截', overview?.blocked, '#cf1322'],
    ['质询', overview?.challenged, '#fa8c16'],
    ['限速', overview?.rate_limited, '#722ed1'],
    ['仅记录', overview?.logged, '#1677ff'],
    ['来源 IP', overview?.unique_ips],
  ];

  const ruleColumns = [
    { title: '规则', dataIndex: 'fw_rule_id', render: (v, r) => <Space size={4}><code>{v}</code>{r.fw_rule_name && r.fw_rule_name !== v ? <span style={{ color: 'rgba(0,0,0,0.45)' }}>{r.fw_rule_name}</span> : null}</Space> },
    { title: '类型', dataIndex: 'fw_rule_type', width: 110 },
    { title: '动作', dataIndex: 'fw_action', width: 100, render: actionTag },
    { title: '命中', dataIndex: 'hits', width: 90, align: 'right', render: num },
    { title: 'IP 数', dataIndex: 'unique_ips', width: 80, align: 'right', render: num },
  ];
  const ipColumns = [
    { title: 'IP', dataIndex: 'client_ip', render: (v) => <code>{v}</code> },
    { title: '国家', dataIndex: 'country_code', width: 80, render: (v) => (v ? <CountryLabel code={v}>{v}</CountryLabel> : '-') },
    { title: '事件', dataIndex: 'events', width: 80, align: 'right', render: num },
    { title: '拦截', dataIndex: 'blocks', width: 80, align: 'right', render: num },
    {
      title: '', key: 'f', width: 70,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => {
          setFilters((f) => ({ ...f, client_ip: r.client_ip }));
          setLogPage(1);
          setApplied((a) => ({ ...a, client_ip: r.client_ip }));
        }}>筛选</Button>
      ),
    },
  ];
  const logColumns = [
    { title: '时间', dataIndex: 'timestamp', width: 160 },
    { title: '主机', dataIndex: 'host', width: 170, ellipsis: true },
    {
      title: '请求', key: 'req', ellipsis: true,
      render: (_, r) => <Tooltip title={r.uri}><span>{r.method} {r.uri}</span></Tooltip>,
    },
    { title: 'IP', dataIndex: 'client_ip', width: 140, render: (v, r) => <span>{v}{r.country_code ? <> · <CountryFlag code={r.country_code} />{r.country_code}</> : ''}</span> },
    { title: '动作', dataIndex: 'fw_action', width: 100, render: actionTag },
    {
      title: '规则', dataIndex: 'fw_rule_id', width: 160, ellipsis: true,
      render: (v, r) => <Tooltip title={[r.fw_message, r.fw_detail, r.fw_matched_key && `${r.fw_matched_key}=${r.fw_matched_value || ''}`].filter(Boolean).join(' | ') || undefined}><span>{r.fw_rule_type ? `${r.fw_rule_type} · ` : ''}{v}</span></Tooltip>,
    },
    { title: '状态', dataIndex: 'status', width: 70 },
  ];

  const panelError = (key) => (errors[key] ? <Alert type="error" showIcon message={`加载失败：${errors[key]}`} /> : null);

  return (
    <div>
      <Spin spinning={loading}>
        {panelError('overview') || (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {stats.map(([label, v, color]) => (
              <Col xs={12} sm={8} lg={4} key={label}>
                <Card size="small"><Statistic title={label} value={num(v)} valueStyle={color ? { color } : undefined} /></Card>
              </Col>
            ))}
          </Row>
        )}

        <Card size="small" title="事件趋势" style={{ marginBottom: 16 }}>
          {panelError('timeline') || (timeline.length
            ? null
            : <Empty description="该时间段无 WAF 事件" image={Empty.PRESENTED_IMAGE_SIMPLE} />)}
          <div ref={chartRef} style={{ width: '100%', height: timeline.length && !errors.timeline ? 260 : 0 }} />
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card size="small" title="命中最多的规则">
              {panelError('rules') || (
                <Table size="small" rowKey={(r) => `${r.fw_rule_id}-${r.fw_action}`} columns={ruleColumns} dataSource={topRules} pagination={false} scroll={{ x: 520 }} locale={{ emptyText: '暂无数据' }} />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card size="small" title="触发最多的 IP">
              {panelError('ips') || (
                <Table size="small" rowKey={(r) => `${r.client_ip}-${r.country_code}`} columns={ipColumns} dataSource={topIps} pagination={false} scroll={{ x: 400 }} locale={{ emptyText: '暂无数据' }} />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>

      <Card
        size="small"
        title="最近事件"
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={() => { loadSummary(); loadLogs(); }}>刷新</Button>}
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="动作"
            style={{ width: 130 }}
            value={filters.action}
            onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
            options={Object.entries(ACTIONS).map(([value, m]) => ({ value, label: m.label }))}
          />
          <Input allowClear placeholder="客户端 IP" style={{ width: 160 }} value={filters.client_ip} onChange={(e) => setFilters((f) => ({ ...f, client_ip: e.target.value }))} onPressEnter={search} />
          <Input allowClear placeholder="URI 包含" style={{ width: 200 }} value={filters.uri} onChange={(e) => setFilters((f) => ({ ...f, uri: e.target.value }))} onPressEnter={search} />
          <Button type="primary" icon={<SearchOutlined />} onClick={search}>查询</Button>
        </Space>
        <Table
          size="small"
          rowKey={(r, i) => r.request_id || `${r._timestamp}-${i}`}
          loading={logsLoading}
          columns={logColumns}
          dataSource={logs}
          scroll={{ x: 1000 }}
          locale={{ emptyText: logsError ? `加载失败：${logsError}` : '暂无事件' }}
          pagination={{
            current: logPage,
            pageSize: logPageSize,
            total: logTotal,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (t) => `共 ${num(t)} 条`,
            onChange: (p, s) => { setLogPage(p); setLogPageSize(s); },
          }}
        />
      </Card>
    </div>
  );
}
